import dayjs from 'dayjs';
import styled from 'styled-components';

import { IconInfo } from 'assets/icons';
import { FixedTicket, ItemState } from 'types/api';

const InfoBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  width: calc(100% - 40px);
  padding: 14px 12px;
  margin: 0 auto;

  border: 1px solid;
  border-color: rgba(234, 235, 236, 1);
  border-radius: 12px;

  margin-bottom: 24px;

  p {
    font-weight: 500;
    font-size: 13px;
    line-height: 18px;

    color: rgba(70, 71, 76, 1);

    &[data-type='warning'] {
      margin-top: 4px;

      font-weight: 400;
      font-size: 11px;
      line-height: 14px;

      color: rgba(255, 66, 66, 1);
    }
  }
`;

const DetailDataBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  width: 100%;
  padding: 0 20px 32px;

  .data-box {
    display: flex;
    flex-direction: column;
    gap: 16px;

    width: 100%;

    > .row {
      display: flex;
      align-items: center;
      gap: 6px;

      > p {
        font-weight: 500;
        font-size: 16px;
        line-height: 24px;

        color: rgba(27, 28, 30, 1);

        word-break: keep-all;
      }
    }
  }

  .paused-row {
    display: flex;
    align-items: center;
    gap: 6px;

    width: 100%;

    p[data-type='name'] {
      font-weight: 500;
      font-size: 16px;
      line-height: 24px;

      color: rgba(27, 28, 30, 1);

      word-break: keep-all;

      width: 100%;
      word-break: break-all;

      /* white-space: nowrap; */

      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;

      &.paused {
        color: #dbdcdf;
      }
    }
  }

  .paused-badge {
    min-width: 32px;

    border-radius: 6px;
    padding: 5px 6px;

    background-color: #ffe5c1;

    font-weight: 600;
    font-size: 11px;
    line-height: 14px;
    text-align: center;
    color: #dd8100;
  }

  .overview-box {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
    gap: 4px;

    width: 100%;

    > .total-box {
      display: flex;
      align-items: center;
      gap: 4px;
      flex-wrap: wrap;

      > p {
        font-weight: 500;

        &[data-type='total-text'] {
          font-size: 16px;
          line-height: 22px;
          color: rgba(27, 28, 30, 1);
        }

        &[data-type='sold-count'] {
          font-size: 20px;
          line-height: 26px;
          color: rgba(0, 113, 233, 1);

          &.warning {
            color: rgba(255, 66, 66, 1);
          }

          &.soldOut {
            color: rgba(112, 115, 124, 1);
          }
        }

        &[data-type='all-count'] {
          font-size: 20px;
          line-height: 26px;
          color: rgba(112, 115, 124, 1);
        }
      }
    }

    p[data-type='remain'] {
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;

      color: rgba(112, 115, 124, 1);

      text-align: right;

      word-break: keep-all;
    }
  }
`;

const SellingStatusSection = ({ item }: { item: FixedTicket }) => {
  const isByItem = item.product.inventoryTargetType === 'BY_ITEM'; // 아이템 마다 재고 있음
  const isBySchedule = item.product.inventoryTargetType === 'BY_SCHEDULE'; // 총 재고만 있음

  const optionItemsList = item.items;
  const noLimitQuota = item.counts.quota === 0;

  const startTime = item?.term.startedAt;
  const endTime = item?.term.endedAt;

  const actionTime = new Date(item.saleTerm.endedAt); // 신청 마감일

  const isUnderMinimumQuota = isBySchedule
    ? item.minimumQuota > item.counts.sale
    : optionItemsList.some(opt => {
        return (
          opt.minimumQuota > opt.counts.sale && opt.status !== ItemState.CLOSED
        );
      });

  const isSoldOut =
    item.counts.quota !== 0 && item.counts.quota === item.counts.sale;

  return (
    <>
      <InfoBox>
        <IconInfo />
        <div>
          <p>
            신청 마감일: {dayjs(actionTime).format('YY년 M월 D일 ddd H:mm')}{' '}
            까지
          </p>

          {isUnderMinimumQuota && (
            <p data-type="warning">
              신청 마감일까지 최소인원 미달 시 일정이 취소될 수 있어요.
            </p>
          )}
        </div>
      </InfoBox>

      <DetailDataBox>
        {isBySchedule ? (
          <>
            <div className="data-box">
              {optionItemsList.map(opt => {
                const optionItemStatus = opt.status;

                const isPaused = optionItemStatus === ItemState.CLOSED;

                return (
                  <div className="paused-row">
                    {isPaused && <div className="paused-badge">중지</div>}
                    <p data-type="name" className={isPaused ? 'paused' : ''}>
                      {opt.name}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="overview-box">
              <div className="total-box">
                <p data-type="total-text">총: </p>
                <p
                  data-type="sold-count"
                  className={
                    isUnderMinimumQuota ? 'warning' : isSoldOut ? 'soldOut' : ''
                  }
                >
                  {item.counts.sale.toLocaleString()}
                </p>
                <p data-type="all-count">
                  /
                  {noLimitQuota
                    ? '제한없음'
                    : item.counts.quota.toLocaleString()}
                </p>
              </div>

              <p data-type="remain">
                {noLimitQuota
                  ? `최소 제한없음 \u2022 남은 수량 제한없음`
                  : `최소 ${item.minimumQuota.toLocaleString()} \u2022 남은 수량 ${
                      item.counts.quota - item.counts.sale > 0
                        ? (
                            item.counts.quota - item.counts.sale
                          ).toLocaleString()
                        : '0'
                    }`}
              </p>
            </div>
          </>
        ) : (
          <div className="data-box">
            {optionItemsList.map(opt => {
              const optionItemStatus = opt.status;

              const isPaused = optionItemStatus === ItemState.CLOSED;

              return (
                <div className="row" key={opt.name}>
                  <div className="paused-row">
                    {isPaused && <div className="paused-badge">중지</div>}
                    <p data-type="name" className={isPaused ? 'paused' : ''}>
                      {opt.name}
                    </p>
                  </div>

                  <div className="overview-box">
                    <div className="total-box">
                      <p data-type="total-text">총: </p>
                      <p
                        data-type="sold-count"
                        className={
                          opt.minimumQuota > opt.counts.sale
                            ? 'warning'
                            : isSoldOut
                            ? 'soldOut'
                            : ''
                        }
                      >
                        {opt.counts.sale.toLocaleString()}
                      </p>
                      <p data-type="all-count">
                        /
                        {noLimitQuota
                          ? '제한없음'
                          : opt.counts.quota.toLocaleString()}
                      </p>
                    </div>

                    <p data-type="remain">
                      {noLimitQuota
                        ? `최소 제한없음 \u2022 남은 수량 제한없음`
                        : `최소 ${opt.minimumQuota.toLocaleString()} \u2022 남은 수량 ${
                            opt.counts.quota - opt.counts.sale > 0
                              ? (
                                  opt.counts.quota - opt.counts.sale
                                ).toLocaleString()
                              : '0'
                          }`}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DetailDataBox>
    </>
  );
};

export default SellingStatusSection;
